import * as React from 'react';
import { memo, useCallback, useState } from 'react';

import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { INotificationPrefs } from 'core/hooks';

import * as S from './NotificationCenter.styled';

interface IProps {
  prefs: INotificationPrefs;
  onStart: () => Promise<{ deepLink: string; expiresAt: string } | null>;
  onDrop: () => Promise<void>;
  onRefetch: () => void;
}

/**
 * Привязка Telegram (ТЗ 0068).
 *
 * 🔴 Поля «введите свой chat_id» здесь нет и быть не должно. Владелец
 * предлагал именно его, но chat_id — непроверяемое число: опечатка отправила
 * бы фамилию и долг студента постороннему человеку, и заметить это было бы
 * нечем. Кнопка выдаёт одноразовую ссылку, а кто нажал в боте START — тот и
 * привязан; сервер узнаёт это от самого Telegram.
 *
 * QR не делаем: библиотеки в проекте нет, а новая зависимость под webpack 4
 * несоразмерна одной картинке. Сотрудник открывает ERP на том же ноутбуке,
 * где стоит Telegram Desktop, а копируемая ссылка закрывает случай «хочу с
 * телефона».
 */
export const TelegramLinkBlock = memo(
  ({ prefs, onStart, onDrop, onRefetch }: IProps) => {
    const { t } = useTranslation();
    const [link, setLink] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [failed, setFailed] = useState(false);

    const start = useCallback(async () => {
      setFailed(false);
      let result = null;
      try {
        result = await onStart();
      } catch {
        // Сетевой сбой иначе стал бы unhandled rejection, а человек — остался
        // бы перед кнопкой, которая «ничего не делает».
        result = null;
      }
      if (!result) {
        // Сервер специально отвечает кодом (например, бот не настроен) — и
        // человек обязан это увидеть, а не смотреть в пустоту.
        setFailed(true);
        return;
      }
      setLink(result.deepLink);
      // Открываем ПЕРВЫМ ЖЕ кликом: `window.open` из отложенного колбэка
      // блокировщик попапов режет молча.
      window.open(result.deepLink, '_blank', 'noopener,noreferrer');
    }, [onStart]);

    const copy = useCallback(() => {
      if (!link) return;
      navigator.clipboard?.writeText(link).then(
        () => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        },
        () => undefined,
      );
    }, [link]);

    const confirmDrop = useCallback(() => {
      Modal.confirm({
        centered: true,
        title: t('notifications.telegram.disconnectConfirm'),
        okText: t('notifications.telegram.disconnect'),
        cancelText: t('button.cancel'),
        onOk: onDrop,
      });
    }, [onDrop, t]);

    return (
      <S.Section>
        <S.SectionTitle>{t('notifications.telegram.title')}</S.SectionTitle>

        {!prefs.telegramAvailable && (
          // Кнопка, которая никуда не ведёт, хуже отсутствующей: человек
          // жмёт её и решает, что сломано у него.
          <S.FieldHint>{t('notifications.telegram.unavailable')}</S.FieldHint>
        )}

        {prefs.telegramAvailable && !prefs.telegramLinked && (
          <>
            <S.FieldHint>{t('notifications.telegram.lead')}</S.FieldHint>
            <S.InlineActions>
              <S.TextButton type="button" onClick={start}>
                {t('notifications.telegram.connect')}
              </S.TextButton>
            </S.InlineActions>
            {failed && (
              <S.Failed>{t('notifications.telegram.failed')}</S.Failed>
            )}
            {link && (
              <>
                <S.Mono>{link}</S.Mono>
                <S.InlineActions>
                  <S.TextButton type="button" onClick={copy}>
                    {copied
                      ? t('notifications.telegram.copied')
                      : t('notifications.telegram.copy')}
                  </S.TextButton>
                  <S.TextButton type="button" onClick={onRefetch}>
                    {t('notifications.telegram.check')}
                  </S.TextButton>
                </S.InlineActions>
                <S.FieldHint>
                  {t('notifications.telegram.pendingHint')}
                </S.FieldHint>
              </>
            )}
          </>
        )}

        {prefs.telegramLinked && (
          <>
            <S.Field>
              <span>
                {t('notifications.telegram.connected', {
                  name: prefs.telegramUsername || '—',
                })}
              </span>
              <S.TextButton type="button" onClick={confirmDrop}>
                {t('notifications.telegram.disconnect')}
              </S.TextButton>
            </S.Field>
            {prefs.telegramBlockedAt && (
              // Иначе человек уверен, что подписан, а канал молча мёртв.
              <S.Failed>{t('notifications.telegram.blocked')}</S.Failed>
            )}
          </>
        )}
      </S.Section>
    );
  },
);

TelegramLinkBlock.displayName = 'TelegramLinkBlock';
